import { useMutation } from '@apollo/client';
import type { CommunityGroup } from '@communities/community-groups/domain';
import { GET_COMMUNITY_GROUPS, CREATE_COMMUNITY_GROUP } from '@communities/community-groups/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface CreateCommunityGroupResponse {
  createCommunityGroup: CommunityGroup;
}

interface CreateCommunityGroupInput {
  name: string;
  description: string;
}

export function useCreateCommunityGroup() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [createCommunityGroupMutation, { loading }] = useMutation<CreateCommunityGroupResponse>(CREATE_COMMUNITY_GROUP, {
    refetchQueries: [{ query: GET_COMMUNITY_GROUPS }],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const createCommunityGroup = async ({ name, description }: CreateCommunityGroupInput): Promise<boolean> => {
    try {
      track('CreateCommunityGroupInitiated', {
        communityGroupName: name,
      });

      await createCommunityGroupMutation({
        variables: {
          input: {
            name,
            description,
          },
        },
      });

      track('CreateCommunityGroupSucceeded', {
        communityGroupName: name,
      });

      return true;
    } catch (error) {
      track('CreateCommunityGroupFailed', {
        communityGroupName: name,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    createCommunityGroup,
    loading,
    errors,
    resetErrors,
  };
}

export default useCreateCommunityGroup;
